/**
 * TaskInvitationActions — botões inline [Aceitar][Recusar] em notification
 * tipo 'task_invitation_received' (UI-3 / TD-V2-PERM-07).
 *
 * Renderizado dentro do <li> da NotificationsBell quando type=task_invitation_received
 * e payload tem invitationId (preferencial) ou taskId (fallback — backend lookup).
 *
 * 409 (already-decided) tratado com mensagem amigável.
 * 503 (feature flag off) mostra mensagem específica.
 *
 * NOTE: o backend cria evento Google Calendar pro invitee quando decision='aceito'
 * (best-effort via DWD). Esta ação é transparente pra UI — só vemos sucesso 200.
 */

import { useState } from 'react';

import { Button } from '@/components/ui';
import { api, ApiError } from '@/lib/api';

interface TaskInvitationActionsProps {
  invitationId: string;
  onDecided: () => void;
}

export function TaskInvitationActions({ invitationId, onDecided }: TaskInvitationActionsProps) {
  const [pending, setPending] = useState<'aceito' | 'recusado' | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handle(decision: 'aceito' | 'recusado') {
    setPending(decision);
    setError(null);
    try {
      await api.put(`/api/v1/task-invitations/${encodeURIComponent(invitationId)}/respond`, {
        decision,
      });
      onDecided();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError('Este convite já foi respondido.');
        onDecided(); // refresh pra ver estado atual
      } else if (err instanceof ApiError && err.status === 403) {
        setError('Este convite não é seu.');
      } else if (err instanceof ApiError && err.status === 503) {
        setError('Convites de tarefa desativados. Pergunte ao admin.');
      } else {
        setError(err instanceof Error ? err.message : 'Erro ao responder convite');
      }
    } finally {
      setPending(null);
    }
  }

  return (
    <div
      data-testid={`invitation-actions-${invitationId}`}
      className="flex flex-col gap-1.5 mt-2"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex gap-1.5">
        <Button
          type="button"
          size="sm"
          variant="primary"
          onClick={() => handle('aceito')}
          loading={pending === 'aceito'}
          disabled={pending !== null}
          data-testid={`invitation-accept-${invitationId}`}
        >
          ✓ Aceitar
        </Button>
        <Button
          type="button"
          size="sm"
          variant="destructive"
          onClick={() => handle('recusado')}
          loading={pending === 'recusado'}
          disabled={pending !== null}
          data-testid={`invitation-decline-${invitationId}`}
        >
          ✕ Recusar
        </Button>
      </div>
      {error && (
        <p
          data-testid={`invitation-error-${invitationId}`}
          className="text-xs text-danger"
        >
          {error}
        </p>
      )}
    </div>
  );
}
